/* global Office, console */

import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { editXMLPart } from "./CustomXmlHelper";
import { XMLParser } from "fast-xml-parser";
import { DocumentParameters } from "../../components/document/ParametersDocument";
import { FormatTypeEnum } from "../../api/models/format-type-enum";

export const CO_OBJECT_NAMESPACE = "CustomXMLCo";
export const CO_ADVANCED_CXML_ID = "Metadata";

const metadataNamespace = CO_OBJECT_NAMESPACE + CO_ADVANCED_CXML_ID;

export interface TemplateInfo {
  templateId: string;
  templateName: string;
}

export interface AdvancedDocumentMetadata {
  numberFormat: string;
  typeNumber: string;
  percentageFormat: string;
  typePercentage: string;
  dateFormat: string;
  typeDate: string;
  choice?: string;
  id: string | null;
  locale: string;
  inheritFormat?: boolean;
  imageExtension?: string;
  imageResolution?: number;
  templateInfo?: TemplateInfo;
}

export interface DocumentMetadata {
  documentId: string;
  language: string;
  advanced?: AdvancedDocumentMetadata;
  parameters?: DocumentParameters[];
}

export const mapAdvancedDocumentToDocPartMetadata = ({
  documentAdvancedCustomXML,
}: {
  documentAdvancedCustomXML: DocumentMetadata;
}): DocumentPartMetadata => {
  return {
    parametersMetadata: documentAdvancedCustomXML.parameters,
    id: documentAdvancedCustomXML.advanced.id,
    documentId: documentAdvancedCustomXML.documentId,
    templateInfo: documentAdvancedCustomXML.advanced.templateInfo,
    formatMetadata: {
      numberFormat: {
        format: documentAdvancedCustomXML.advanced.numberFormat,
      },
      numberType: documentAdvancedCustomXML.advanced.typeNumber,
      percentageFormat: {
        format: documentAdvancedCustomXML.advanced.percentageFormat,
      },
      percentageType: documentAdvancedCustomXML.advanced.typePercentage,
      dateFormat: {
        format: documentAdvancedCustomXML.advanced.dateFormat,
      },
      dateType: documentAdvancedCustomXML.advanced.typeDate,
      locale: documentAdvancedCustomXML.advanced.locale,
    },
    language: documentAdvancedCustomXML.language,
    formatImage: {
      imageExtension: documentAdvancedCustomXML.advanced.imageExtension,
      ...(documentAdvancedCustomXML.advanced.imageResolution
        ? { imageResolution: documentAdvancedCustomXML.advanced.imageResolution }
        : {}),
    },
  };
};

function fixDocumentParameterParse(xmlObject) {
  const isDefinedButNotArray = (value: any) => value != null && !Array.isArray(value);
  if (xmlObject.parameters) {
    const parameters = isDefinedButNotArray(xmlObject.parameters) ? [xmlObject.parameters] : xmlObject.parameters;
    xmlObject.parameters = parameters.map((parameter) => {
      return {
        ...parameter,
        dimensions: (isDefinedButNotArray(parameter.dimensions) ? [parameter.dimensions] : parameter.dimensions).map(
          (dimension) => {
            return {
              ...dimension,
              values: (isDefinedButNotArray(dimension.values) ? [dimension.values] : dimension.values).map((value) => {
                return {
                  ...value,
                  oidPath: isDefinedButNotArray(value.oidPath) ? [value.oidPath] : value.oidPath,
                };
              }),
            };
          }
        ),
      };
    });
  }
}

const option = { ignoreAttributes: false, numberParseOptions: { leadingZeros: false, hex: true } };
function buildDocumentAdvancedFromXMLString(xml: string): DocumentMetadata {
  const xmlObject = new XMLParser(option).parse(xml)[CO_OBJECT_NAMESPACE];
  fixDocumentParameterParse(xmlObject);
  return xmlObject;
}

export const getDocumentAdvancedCustomXml = (): Promise<DocumentMetadata> => {
  return new Promise((resolve) => {
    try {
      Office.context.document.customXmlParts.getByNamespaceAsync(metadataNamespace, function (result) {
        const xmlPart = result.value[0];
        if (xmlPart) {
          xmlPart.getXmlAsync({}, function (eventArgs) {
            resolve(buildDocumentAdvancedFromXMLString(eventArgs.value));
          });
        } else resolve(null);
      });
    } catch (e) {
      console.error(e);
      resolve(null);
    }
  });
};

export function editDocumentAdvancedCustomXML(dataObject: DocumentMetadata) {
  editXMLPart(CO_ADVANCED_CXML_ID, dataObject);
}
